import { AGING_BUCKETS, type AgingRow, type CloseRate, type JobPnlRow, type LeadSourceRow, type PipelineRow, type ReportsOverview, type SatisfactionReport, percentChange } from "@nmt/shared";
import { InsuranceList } from "../components/insurance";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { get } from "../api";
import { useSession } from "../session";
import { Empty, Field, Loading, PageHeader, Panel, PanelHeader, cx, formatCents, formatDate } from "../components/ui";

const iso = (d: Date) => d.toLocaleDateString("en-CA");

function presets() {
  const now = new Date();
  const y = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3);
  return [
    { label: "This year", from: `${y}-01-01`, to: iso(now) },
    { label: "This quarter", from: iso(new Date(y, q * 3, 1)), to: iso(now) },
    { label: "Last 30 days", from: iso(new Date(now.getTime() - 29 * 86_400_000)), to: iso(now) },
    { label: "Last year", from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
  ];
}

function Delta({ now, before }: { now: number; before: number }) {
  const p = percentChange(now, before);
  if (p === null) return <span className="text-xs text-muted">no prior year</span>;
  return <span className={cx("text-xs font-semibold", p >= 0 ? "text-ok" : "text-danger")}>{p >= 0 ? "▲" : "▼"} {Math.abs(p)}% vs last year</span>;
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-panel)] bg-white px-5 py-4 shadow-[var(--shadow-card)]">
      <div className="text-xs font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}

/** Business overview and reports (admins). */
export function ReportsPage() {
  const opts = presets();
  const [range, setRange] = useState({ from: opts[0]!.from, to: opts[0]!.to });
  const qs = `from=${range.from}&to=${range.to}`;
  // Each report shows only with its own permission.
  const { can } = useSession();
  const overview = useQuery({ queryKey: ["reports", "overview", qs], queryFn: () => get<ReportsOverview>(`/api/reports/overview?${qs}`), enabled: can("reports.overview", "reports.leaderboard") });
  const pipeline = useQuery({ queryKey: ["reports", "pipeline"], queryFn: () => get<PipelineRow[]>("/api/reports/pipeline"), enabled: can("reports.pipeline") });
  const aging = useQuery({ queryKey: ["reports", "aging"], queryFn: () => get<AgingRow[]>("/api/reports/ar-aging"), enabled: can("reports.aging") });
  const close = useQuery({ queryKey: ["reports", "close", qs], queryFn: () => get<CloseRate>(`/api/reports/close-rate?${qs}`), enabled: can("reports.closeRate") });
  const sources = useQuery({ queryKey: ["reports", "sources", qs], queryFn: () => get<LeadSourceRow[]>(`/api/reports/lead-sources?${qs}`), enabled: can("reports.leadSources") });
  const happy = useQuery({ queryKey: ["reports", "satisfaction", qs], queryFn: () => get<SatisfactionReport>(`/api/reports/satisfaction?${qs}`), enabled: can("reports.satisfaction") });
  const pnl = useQuery({ queryKey: ["reports", "pnl", qs], queryFn: () => get<JobPnlRow[]>(`/api/reports/job-pnl?${qs}`), enabled: can("reports.jobProfit") });
  const o = overview.data;
  const ranged = can("reports.overview", "reports.leaderboard", "reports.satisfaction", "reports.closeRate", "reports.leadSources", "reports.jobProfit");
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Reports" description="Sales are signed proposals; collected is payments received. Compared with the same dates last year." />
      {ranged && (
      <div className="flex flex-wrap items-end gap-2">
        {opts.map((p) => (
          <button key={p.label} onClick={() => setRange({ from: p.from, to: p.to })} className={cx("rounded-full px-3 py-1.5 text-sm font-semibold", range.from === p.from && range.to === p.to ? "bg-ink text-white" : "bg-white text-ink-soft shadow-[var(--shadow-card)] hover:text-ink")}>
            {p.label}
          </button>
        ))}
        <Field label="From" type="date" value={range.from} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} className="w-40" />
        <Field label="To" type="date" value={range.to} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} className="w-40" />
      </div>
      )}
      {!can("reports.overview") ? null : !o?.now || !o.priorYear ? (
        <Loading />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Sales" value={formatCents(o.now.salesCents)} sub={<><span className="mr-2 text-xs text-muted">{o.now.salesCount} signed</span><Delta now={o.now.salesCents} before={o.priorYear.salesCents} /></>} />
          <Stat label="Collected" value={formatCents(o.now.collectedCents)} sub={<Delta now={o.now.collectedCents} before={o.priorYear.collectedCents} />} />
          <Stat label="New jobs" value={String(o.now.newJobs)} sub={<Delta now={o.now.newJobs} before={o.priorYear.newJobs} />} />
          <Stat label="Owed to us now" value={formatCents(o.arOutstandingCents ?? 0)} sub={<span className="text-xs text-muted">unpaid invoice balances</span>} />
          <Stat label="Depreciation to collect" value={formatCents(o.depreciationToCollectCents ?? 0)} sub={<span className="text-xs text-muted">recoverable depreciation on signed claims, not yet collected</span>} />
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        {can("reports.leaderboard") && (
        <Panel>
          <PanelHeader title="Rep leaderboard" />
          {!o?.leaderboard.length ? <Empty>No sales in this range.</Empty> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted uppercase"><tr><th className="px-5 py-2">Rep</th><th className="py-2 text-right">Signed</th><th className="py-2 text-right">Sales</th><th className="px-5 py-2 text-right">Collected</th></tr></thead>
              <tbody>
                {o.leaderboard.map((r) => (
                  <tr key={r.rep} className="border-t border-mist">
                    <td className="px-5 py-2"><span className="mr-2 inline-block size-2.5 rounded-full" style={{ background: r.color }} />{r.rep}</td>
                    <td className="py-2 text-right">{r.salesCount}</td>
                    <td className="py-2 text-right font-semibold">{formatCents(r.salesCents)}</td>
                    <td className="px-5 py-2 text-right">{formatCents(r.collectedCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        )}
        {can("reports.satisfaction") && (
        <Panel>
          <PanelHeader title="Customer satisfaction" description='"How did we do?" surveys sent in this range (when jobs closed).' />
          {!happy.data ? <Loading /> : !happy.data.sent ? <Empty>No surveys sent in this range.</Empty> : (
            <div className="flex flex-col gap-3 px-5 py-4 text-sm">
              <div className="flex flex-wrap items-baseline gap-4">
                <div><span className="text-2xl font-semibold">{happy.data.average ?? "—"}</span> <span className="text-muted">average of 5</span></div>
                <div className="text-muted">{happy.data.answered} of {happy.data.sent} answered</div>
              </div>
              <div className="flex gap-2">
                {happy.data.counts.map((n, i) => (
                  <div key={i} className="flex-1 rounded bg-mist px-2 py-1 text-center"><div className="text-amber-500">{"★".repeat(i + 1)}</div><div className="font-semibold">{n}</div></div>
                ))}
              </div>
              {happy.data.recent.filter((r) => r.comment).slice(0, 5).map((r) => (
                <div key={r.jobId} className="border-t border-mist pt-2"><span className="font-semibold">{r.score}/5</span> · job #{r.jobNumber} {r.customer}: <span className="text-ink-soft">{r.comment}</span></div>
              ))}
            </div>
          )}
        </Panel>
        )}
        {can("reports.closeRate") && (
        <Panel>
          <PanelHeader title="Proposal close rate" description="Proposals sent in this range." />
          {!close.data ? <Loading /> : (
            <div className="grid grid-cols-2 gap-3 px-5 py-4 text-sm sm:grid-cols-4">
              <div><div className="text-2xl font-semibold">{close.data.ratePercent === null ? "—" : `${close.data.ratePercent}%`}</div><div className="text-muted">closed</div></div>
              <div><div className="text-2xl font-semibold">{close.data.signed}</div><div className="text-muted">signed ({formatCents(close.data.signedCents)})</div></div>
              <div><div className="text-2xl font-semibold">{close.data.declined}</div><div className="text-muted">declined</div></div>
              <div><div className="text-2xl font-semibold">{close.data.open}</div><div className="text-muted">still out</div></div>
            </div>
          )}
        </Panel>
        )}
      </div>
      {can("reports.aging") && (
      <Panel>
        <PanelHeader title="Money owed (aging)" description="Unpaid invoice balances by days past due (due date, or 30 days after sending)." />
        {!aging.data ? <Loading /> : !aging.data.length ? <Empty>Nothing owed.</Empty> : (
          <>
            <div className="grid grid-cols-5 gap-2 px-5 py-4 text-sm">
              {AGING_BUCKETS.map((b) => (
                <div key={b}>
                  <div className="text-xs text-muted uppercase">{b === "current" ? "Not due" : `${b} days`}</div>
                  <div className="font-semibold">{formatCents(aging.data.filter((r) => r.bucket === b).reduce((s, r) => s + r.balanceCents, 0))}</div>
                </div>
              ))}
            </div>
            <ul className="border-t border-mist text-sm">
              {aging.data.map((r) => (
                <li key={r.invoiceId} className="flex gap-3 border-b border-mist px-5 py-2 last:border-0">
                  <Link to={`/jobs/${r.jobId}`} className="hover:text-brand">Invoice {r.number} · {r.jobLabel}</Link>
                  <span className="ml-auto text-muted">due {formatDate(r.due)}</span>
                  <span className="w-28 text-right font-semibold">{formatCents(r.balanceCents)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        {can("reports.pipeline") && (
        <Panel>
          <PanelHeader title="Pipeline" description="Jobs in each stage now, with signed contract value." />
          {!pipeline.data ? <Loading /> : (
            <table className="w-full text-sm">
              <tbody>
                {pipeline.data.filter((p) => p.jobs > 0).map((p) => (
                  <tr key={`${p.workflow}-${p.stage}`} className="border-t border-mist first:border-0">
                    <td className="px-5 py-2 text-muted">{p.workflow}</td>
                    <td className="py-2">{p.stage}</td>
                    <td className="py-2 text-right">{p.jobs}</td>
                    <td className="px-5 py-2 text-right">{p.contractCents ? formatCents(p.contractCents) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        )}
        {can("reports.leadSources") && (
        <Panel>
          <PanelHeader title="Lead sources" description="Jobs added in this range, and how many sold." />
          {!sources.data?.length ? <Empty>No jobs added in this range.</Empty> : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted uppercase"><tr><th className="px-5 py-2">Source</th><th className="py-2 text-right">Jobs</th><th className="py-2 text-right">Sold</th><th className="px-5 py-2 text-right">Value</th></tr></thead>
              <tbody>
                {sources.data.map((r) => (
                  <tr key={r.source} className="border-t border-mist">
                    <td className="px-5 py-2">{r.source}</td>
                    <td className="py-2 text-right">{r.jobs}</td>
                    <td className="py-2 text-right">{r.sold}</td>
                    <td className="px-5 py-2 text-right">{formatCents(r.contractCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        )}
      </div>
      {can("reports.jobProfit") && (
      <Panel>
        <PanelHeader title="Job profit" description="Contract and collections against costs: approved receipts and bills, and completed work orders. Jobs changed in this range." />
        {!pnl.data?.length ? <Empty>Nothing in this range.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted uppercase"><tr><th className="px-5 py-2">Job</th><th className="py-2">Stage</th><th className="py-2 text-right">Contract</th><th className="py-2 text-right">Collected</th><th className="py-2 text-right">Costs</th><th className="px-5 py-2 text-right">Margin</th></tr></thead>
              <tbody>
                {pnl.data.map((r) => (
                  <tr key={r.jobId} className="border-t border-mist">
                    <td className="px-5 py-2"><Link to={`/jobs/${r.jobId}`} className="hover:text-brand">{r.jobLabel}</Link></td>
                    <td className="py-2 text-muted">{r.stage}</td>
                    <td className="py-2 text-right">{formatCents(r.contractCents)}</td>
                    <td className="py-2 text-right">{formatCents(r.collectedCents)}</td>
                    <td className="py-2 text-right">{formatCents(r.costCents)}</td>
                    <td className={cx("px-5 py-2 text-right font-semibold", r.marginCents < 0 && "text-danger")}>{formatCents(r.marginCents)}{r.marginPercent !== null && <span className="ml-1 text-xs font-normal text-muted">{r.marginPercent}%</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      )}
      {can("workorders.edit", "customers.edit") && (
        <Panel id="insurance">
          <PanelHeader title="Sub insurance" description="Workers' comp, general liability, and W-9 for every subcontractor. Subs are emailed before a policy expires and when it does, with a link to upload the new certificate." />
          <InsuranceList all />
        </Panel>
      )}
    </div>
  );
}
